// src/components/StationIssue/StationTimeSeries.tsx
import React, {useMemo, useState, useCallback, useRef, useEffect} from "react";
import { useParams, useSearchParams } from 'react-router-dom';
import DateRangeFilter from "../Filter/DateRangeFilter";
import PrecipitationChart from "../TimeSeriesChart/PrecipitationChart";
import PrecipitationComparisonChart from "../TimeSeriesChart/PrecipitationComparisonChart";
import LightningPrecipitationChart from "../TimeSeriesChart/LightningPrecipitationChart";
import BatterySolarChart from "../TimeSeriesChart/BatterySolarChart";
import BatteryVoltageChart from "../TimeSeriesChart/BatteryVoltageChart";
import TemperatureHumidityPressureChart from "../TimeSeriesChart/TemperatureHumidityPressureChart";
import WindSpeedDirectionChart from "../TimeSeriesChart/WindSpeedDirectionChart";
import SolarRadiationChart from "../TimeSeriesChart/SolarRadiationChart";
import VoltageTimeSeriesChart from "../TimeSeriesChart/VoltageChart";
import TiltChart from "../TimeSeriesChart/TiltChart";
import { CATEGORY_VARIABLES, VARIABLE_NAMES, Y_VARIABLE_NAMES } from '../../types/issue_variables.type';
import moment from 'moment';
import "./StationTimeSeries.css";
import { getStation } from '../../services/api.service';
import IDeployedStation from "../../types/deployed_station.type";


// Define filter interface
interface FilterOption {
    key: string;
    label: string;
    icon: string;
    color: string;
    defaultEnabled: boolean;
}

// Define valid category keys
type CategoryKey = 'precipitation' | 'battery' | 'sensor';


// Filter configuration for each category
const CATEGORY_FILTERS:  Record<CategoryKey, FilterOption[]> = {
    'precipitation': [
        { key: 'dripClog', label: 'Drip Clog Issues', icon: 'fa-tint', color: 'warning', defaultEnabled: true },
        { key: 'qcIssues', label: 'TAHMO Precip Flags', icon: 'fa-flag', color: 'danger', defaultEnabled: true },
        { key: 'satelliteComparison', label: 'Satellite Comparison', icon: 'fa-satellite', color: 'info', defaultEnabled: true },
        { key: 'lightningPrecipitation', label: 'Lightning vs Precipitation', icon: 'fa-satellite', color: 'info', defaultEnabled: true },
    ],
    'battery': [
        { key: 'severeBatteryIssues', label: 'Severe Battery Issues', icon: 'fa-battery-quarter', color: 'danger', defaultEnabled: true },
        { key: 'imminentFailure', label: 'Imminent Failure', icon: 'fa-line-chart', color: 'success', defaultEnabled: true },
        { key: 'rapidDrop', label: 'Rapid Battery Drop', icon: 'fa-battery-quarter', color: 'danger', defaultEnabled: true },
        { key: 'lowBatteryCapacity', label: 'Low Battery Capacity', icon: 'fa-battery-empty', color: 'warning', defaultEnabled: true },
    ],
    'sensor': [
        { key: 'stormyNoRain', label: 'Stormy No Rain', icon: 'fa-cloud-bolt', color: 'warning', defaultEnabled: true },
        { key: 'tahmoQCHumiditySevere', label: 'TAHMO QC: Humidity Severe', icon: 'fa-water', color: 'warning', defaultEnabled: true },
        { key: 'tahmoQCSolarRadiation', label: 'TAHMO QC: Solar radiation Severe', icon: 'fa-sun', color: 'warning', defaultEnabled: true },
    ]
};


const StationTimeSeries: React.FC = () => {
   const { stationCode } = useParams();
       const [searchParams, setSearchParams] = useSearchParams();
    const {category} = useParams();
     const [stationName, setStationName] = useState<string>('');
    const [visibleCharts, setVisibleCharts] = useState<string[]>([]);
    const [filters, setFilters] = useState<Record<string, boolean>>({});
    const observerRef = useRef<IntersectionObserver | null>(null);
    const elementRefs = useRef<Map<string, HTMLDivElement>>(new Map());

      // Memoize date calculations (strings in YYYY-MM-DDTHH:mm:ss)
     useEffect(() => {
        if (!stationCode) return;
        const fetchName = async () => {
          try {
            const res = await getStation(stationCode);
            // Adjust to your API response keys
            setStationName(res.data.stationName || res.data.name || '');
          } catch {
            setStationName('');
          }
        };
        fetchName();
      }, [stationCode])


      // Memoize date calculations
    const { startDate, endDate } = useMemo(() => ({
        startDate: searchParams.get('start_date') ||
            moment().subtract(7, 'days').format('YYYY-MM-DDTHH:mm:ss'),
        endDate: searchParams.get('end_date') ||
            moment().format('YYYY-MM-DDTHH:mm:ss')
    }), [searchParams]);

    // Memoize variables array
    const variables = useMemo(() =>
        category ? CATEGORY_VARIABLES[category as CategoryKey] : [],
        [category]
    );

       // Get category filters with type safety
    const categoryFilters = useMemo(() => {
        if (!category) return [];
        return CATEGORY_FILTERS[category as CategoryKey] || [];
    }, [category]);

     // Initialize filters based on category
    useEffect(() => {
        if (categoryFilters.length > 0) {
            const initialFilters: Record<string, boolean> = {};
            categoryFilters.forEach((filter: FilterOption) => {
                initialFilters[filter.key] = filter.defaultEnabled;
            });
            setFilters(initialFilters);
        }
    }, [categoryFilters]);


        // Toggle filter function
    const toggleFilter = useCallback((filterKey: string) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: !prev[filterKey]
        }));
    }, []);

         // Reset all filters to default
    const resetFilters = useCallback(() => {
        if (categoryFilters.length > 0) {
            const resetFilters: Record<string, boolean> = {};
            categoryFilters.forEach((filter: FilterOption) => {
                resetFilters[filter.key] = filter.defaultEnabled;
            });
            setFilters(resetFilters);
        }
    }, [categoryFilters]);

    // DateRangeFilter handlers: update URL query params
    const handleApplyDateRange = useCallback((newStart: string, newEnd: string) => {
        const next = new URLSearchParams(searchParams.toString());
        if (newStart) next.set('start_date', newStart); else next.delete('start_date');
        if (newEnd) next.set('end_date', newEnd); else next.delete('end_date');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleClearDateRange = useCallback(() => {
        const next = new URLSearchParams(searchParams.toString());
        next.delete('start_date');
        next.delete('end_date');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

      useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const chartId = (entry.target as HTMLElement).dataset.chartId;
                        if (chartId) {
                            setVisibleCharts(prev => {
                                if (!prev.includes(chartId)) {
                                    return [...prev, chartId];
                                }
                                return prev;
                            });
                        }
                    }
                });
            },
            {
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        // Observe all existing elements
        elementRefs.current.forEach((element) => {
            if (observerRef.current) {
                observerRef.current.observe(element);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [category]);
  

      // Ref callback to register elements
    const setElementRef = (chartId: string) => (element: HTMLDivElement | null) => {
        if (element) {
            elementRefs.current.set(chartId, element);
            if (observerRef.current) {
                observerRef.current.observe(element);
            }
        } else {
            const existingElement = elementRefs.current.get(chartId);
            if (existingElement && observerRef.current) {
                observerRef.current.unobserve(existingElement);
            }
            elementRefs.current.delete(chartId);
        }
    };

    const getFilterDescription = (filterKey: string): string => {
        const descriptions: Record<string, string> = {
            'dripClog': 'Show periods with precipitation sensor blockages',
            'qcIssues': 'Highlight data points flagged by quality control',
            'satelliteComparison': 'Compare station data with satellite estimates',
            'lightningPrecipitation': 'Lightning Activity with no precipitation',
            'badSatellite': 'Mark periods where satellite data is unreliable',
            'severeBattery': 'Highlight critical battery level periods',
            'batteryTrends': 'Show battery performance trends over time',
            'temperatureAlerts': 'Mark extreme temperature events',
            'pressureAlerts': 'Show atmospheric pressure anomalies',
            'sensorMalfunctions': 'Highlight sensor failure periods',
            'calibrationIssues': 'Show periods needing sensor calibration',
            'dataGaps': 'Mark missing data periods',
            'outlierDetection': 'Highlight statistical outliers in data',
            'allQcFlags': 'Show all quality control flags',
            'dataQuality': 'Display overall data quality scores',
            'completeness': 'Show data completeness indicators',
            'consistencyChecks': 'Highlight data consistency issues'
        };
        return descriptions[filterKey] || 'Toggle this data overlay';
    };


const toggleAllFilters = (enabled: boolean) => {
    const newFilters: Record<string, boolean> = {};
    categoryFilters.forEach((filter: FilterOption) => {
        newFilters[filter.key] = enabled;
    });
    setFilters(newFilters);
};

    if (!stationCode || !category) {
        return <div>Missing station ID or category</div>;
    }


    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    {/* Header */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                                <div>
                                    <h4 className="page-title">
                                        Station {stationCode}{stationName ? ` - ${stationName}` : ''} - {category}
                                    </h4>
                                   
                                </div>

                                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                                  <DateRangeFilter
                                    startDate={startDate}
                                    endDate={endDate}
                                    onApply={handleApplyDateRange}
                                    onClear={handleClearDateRange}
                                  />

                                  <p className="text-muted mb-0">
                                    Showing data from {moment(startDate).format('YYYY-MM-DD HH:mm')} to {moment(endDate).format('YYYY-MM-DD HH:mm')}
                                  </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {categoryFilters.length > 0 && (
    <div className="row mb-4">
        <div className="col-12">
            <div className="card shadow-sm border-0">
                <div className="card-header bg-gradient-primary text-white border-0">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <i className="fa fa-sliders-h fa-lg"></i>
                            </div>
                            <div>
                                <h5 className="card-title mb-0 fw-bold">Chart Annotations & Filters</h5>
                                <small className="opacity-75">Customize what data overlays are shown on the charts</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-light text-dark px-2 py-1">
                                <strong>{Object.values(filters).filter(Boolean).length}</strong>
                                <span className="opacity-75"> / {categoryFilters.length} active</span>
                            </span>
                            <button
                                className="btn btn-sm btn-outline-light border-0 px-3"
                                onClick={resetFilters}
                                title="Reset all filters to default"
                            >
                                <i className="fa fa-refresh me-1"></i>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body p-4 bg-light">
                    <div className="row g-4">
                        {categoryFilters.map((filter: FilterOption) => (
                            <div key={filter.key} className="col-sm-6 col-lg-4 col-xl-3">
                                <div className={`filter-card h-100 p-3 rounded-3 border-2 transition-all ${
                                    filters[filter.key] 
                                        ? `border-${filter.color} bg-${filter.color} bg-opacity-10` 
                                        : 'border-light bg-white'
                                }`}>
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            className={`form-check-input me-3 filter-switch filter-switch-${filter.color}`}
                                            type="checkbox"
                                            id={`filter-${filter.key}`}
                                            checked={filters[filter.key] || false}
                                            onChange={() => toggleFilter(filter.key)}
                                        />
                                        <label
                                            className="form-check-label d-flex align-items-center cursor-pointer w-100 user-select-none"
                                            htmlFor={`filter-${filter.key}`}
                                        >
                                            <div className="d-flex align-items-center w-100">
                                                <div className={`filter-icon me-3 d-flex align-items-center justify-content-center rounded-circle bg-${filter.color} text-white`}
                                                     style={{ width: '36px', height: '36px', fontSize: '14px' }}>
                                                    <i className={`fa ${filter.icon}`}></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-semibold text-dark mb-1" style={{ fontSize: '14px', lineHeight: '1.2' }}>
                                                        {filter.label}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: '11px' }}>
                                                        {getFilterDescription(filter.key)}
                                                    </div>
                                                </div>
                                                <div className="ms-2">
                                                    {filters[filter.key] ? (
                                                        <i className={`fa fa-check-circle text-${filter.color}`}></i>
                                                    ) : (
                                                        <i className="fa fa-circle-o text-muted"></i>
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-4 pt-3 border-top">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => toggleAllFilters(true)}
                                >
                                    <i className="fa fa-check-square me-1"></i>
                                    Enable All
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => toggleAllFilters(false)}
                                >
                                    <i className="fa fa-square-o me-1"></i>
                                    Disable All
                                </button>
                            </div>
                            <small className="text-muted d-flex align-items-center">
                                <i className="fa fa-info-circle me-2"></i>
                                Filters control chart annotations and data overlays in real-time
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}



                    {/* Charts */}
                    <div className="row">
                           {category === 'precipitation' && (
                            <>
                                <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('precipitation')}
                                        data-chart-id="precipitation"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('precipitation') ? (
                                            <PrecipitationChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showDripClog={filters.dripClog}
                                                showQCIssues={filters.qcIssues}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-chart-line fa-3x mb-3"></i>
                                                        <p>Chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('comparison')}
                                        data-chart-id="comparison"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('comparison') ? (
                                            <PrecipitationComparisonChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showSatelliteComparison={filters.satelliteComparison}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-chart-line fa-3x mb-3"></i>
                                                        <p>Comparison chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('lightning')}
                                        data-chart-id="lightning"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('lightning') ? (
                                            <LightningPrecipitationChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showLightningNoPrecipitation={filters.lightningPrecipitation}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-bolt fa-3x mb-3"></i>
                                                        <p>Lightning chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </>
                        )}

                        {category === 'battery' && (

                            <>
                              <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('battery-solar')}
                                        data-chart-id="battery-solar"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('battery-solar') ? (
                                            <BatterySolarChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showSevereBatteryIssues={filters.severeBatteryIssues}
                                                showImminentBatteryFailure={filters.imminentFailure}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Battery vs Solar comparison chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('voltage')}
                                        data-chart-id="voltage"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('voltage') ? (
                                            <VoltageTimeSeriesChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                 title="Voltage vs 100% Battery Comparison"
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Voltage comparison chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-xl-12">
                                    <div
                                        ref={setElementRef('battery-voltage')}
                                        data-chart-id="battery-voltage"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('battery-voltage') ? (
                                            <BatteryVoltageChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showSevereBatteryIssues={filters.severeBatteryIssues}
                                                showImminentBatteryFailure={filters.imminentFailure}
                                                showRapidBatteryDrop={filters.rapidDrop}
                                                showLowBatteryCapacity={filters.lowBatteryCapacity}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-battery-half fa-3x mb-3"></i>
                                                        <p>Battery level vs voltage chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>


                            </>
                        )}

                          {category === 'sensor' && (
                              <>
                                  <div className="col-xl-12">
                                       <div
                                        ref={setElementRef('temp-humidity-pressure')}
                                        data-chart-id="temp-humidity-pressure"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('temp-humidity-pressure') ? (
                                            <TemperatureHumidityPressureChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showStormyNoRain={filters.stormyNoRain}
                                                showTahmoQCHumiditySevere={filters.tahmoQCHumiditySevere}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Temperature, Humidity and Pressure comparison chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                  </div>

                                  <div className="col-xl-12">
                                       <div
                                        ref={setElementRef('wind-speed-direction')}
                                        data-chart-id="wind-speed-direction"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('wind-speed-direction') ? (
                                            <WindSpeedDirectionChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Wind Speed and Wind Direction comparison chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                  </div>

                                   <div className="col-xl-12">
                                       <div
                                        ref={setElementRef('tilt')}
                                        data-chart-id="tilt"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('tilt') ? (
                                            <TiltChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Station Tilt chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                  </div>


                                   <div className="col-xl-12">
                                       <div
                                        ref={setElementRef('solar-radiation')}
                                        data-chart-id="solar-radiation"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {visibleCharts.includes('solar-radiation') ? (
                                            <SolarRadiationChart
                                                stationId={stationCode}
                                                startDate={startDate}
                                                endDate={endDate}
                                                height={400}
                                                showTahmoQCSolarRadiation={filters.tahmoQCSolarRadiation}
                                            />
                                        ) : (
                                            <div className="card">
                                                <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <div className="text-muted">
                                                        <i className="fa fa-line-chart fa-3x mb-3"></i>
                                                        <p>Solar radiation chart will load when visible</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                  </div>
                              </>
                          )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default StationTimeSeries;


























